import { ref } from 'vue';

export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export function isMobileBrowser() {
    if (typeof navigator === 'undefined') return false;

    // 1) Prefer Client Hints if available (modern browsers)
    const uaData = navigator.userAgentData;
    if (uaData && typeof uaData.mobile === 'boolean') {
        return uaData.mobile;
    }

    // 2) Use pointer coarse (touch-centric devices) as feature-detect
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
        return true;
    }

    // 3) Small viewport heuristic
    if (window.innerWidth && window.innerWidth <= 800) {
        // tune threshold to your audience
        return true;
    }

    // 4) Conservative UA regex fallback
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}


// ref будет хранить объект события, когда оно сработает.
// Изначально null.
/** @type {import('vue').Ref<BeforeInstallPromptEvent | null>} */
export const deferredPromptRef = ref(null);

/**
 * Определяет тип браузера пользователя для целей PWA-инсталляции,
 * основываясь на строке User Agent.
 *
 * @returns {'chrome' | 'safari' | 'other'} Строка, указывающая на тип браузера.
 * - 'chrome': Браузеры на базе Chromium (Chrome, Edge и др.), поддерживающие beforeinstallprompt.
 * - 'safari': Мобильный Safari на iOS/iPadOS, требующий ручной установки.
 * - 'other': Все остальные браузеры (Firefox, десктопный Safari и т.д.).
 */
export function getBrowserType() {
    const ua = navigator.userAgent;

    if ((/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && ua.includes('Safari') && !ua.includes('CriOS') && !ua.includes('FxiOS')) {
        return 'safari';
    }

    if (ua.includes('Chrome') || ua.includes('CriOS') || ua.includes('Edg')) {
        // Дополнительная проверка, чтобы отсечь Firefox (если он маскируется) и Safari (если он не на iOS)
        if (!ua.includes('Firefox') && !ua.includes('Edg')) {
            return 'chrome';
        }
    }

    return 'other';
}

/**
 * Проверяет, открыта ли текущая страница в режиме PWA (автономный режим).
 * * @returns {boolean} Возвращает true, если страница открыта в режиме PWA (standalone, fullscreen и т.п.).
 */
export function isPWAInstalled() {
    // Проверка через медиа-запрос display-mode (для большинства современных браузеров, включая Chromium)
    const displayModes = ['fullscreen', 'standalone', 'minimal-ui'];
    if (displayModes.some(mode => window.matchMedia(`(display-mode: ${mode})`).matches)) {
        return true;
    }

    // Дополнительная проверка для Safari на iOS (устаревшее, но актуальное свойство)
    return !!('standalone' in window.navigator && window.navigator.standalone);
}

/**
 * Асинхронно проверяет, установлено ли связанное PWA или нативное приложение
 * с использованием Get Installed Related Apps API.
 * * @returns {Promise<boolean>} Промис, который разрешается в true, если приложение установлено.
 */
export async function isAppInstalledUsingApi() {
    if ('getInstalledRelatedApps' in navigator) {
        try {
            const relatedApps = await navigator.getInstalledRelatedApps();
            return relatedApps.length > 0;
        } catch (error) {
            console.error("Ошибка при вызове getInstalledRelatedApps:", error);
            return false;
        }
    }

    return false;
}

/**
 * Асинхронно проверяет, подписан ли пользователь на Push-уведомления.
 * * @returns {Promise<boolean>} Промис, который разрешается в true, если подписка существует.
 */
export const isUserSubscribed = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return false;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        return !!subscription;

    } catch (error) {
        // На случай ошибки при доступе к Service Worker'у или PushManager
        console.error("Ошибка при проверке подписки на уведомления:", error);
        return false;
    }
}
